import { HitData } from 'common/store/common/search/types';
import { getArraysDifference } from 'common/util/utils';
import store from '../../../store';
import { GradingWorkflowSettingsType, QuestionAssignments, WorkflowSettingsTabType } from '../types';
import {
    CompositeCalculationRule,
    GradingWorkflowAllocationUnit,
    QuestionAssignmentEditLevel,
    workflowSettingsTabs,
} from '../constants';
import { QuestionEvaluatorAssignment } from '../../table/types';
import {
    getMarksModeForTestOrCommittee,
    getQuestionAssignmentAdminLevelForTestOrCommittee,
    GRADING_MODE,
} from '../../../services/workflowService';
import { RootState } from '../../../store/rootReducer';
import {
    hasCurrentUserEvaluatorPrivilege,
    hasCurrentUserPlannerPrivilege, isGradingCompleteForCommitteeOrTest,
} from '../../../services/permissions/PermissionService';

export const getDraftTypeByActiveTab = (activeTabIndex: number, tabsGroup: GradingWorkflowSettingsType) => {
    const activeTab: WorkflowSettingsTabType = Object.values(workflowSettingsTabs)
        .find((tabInfo: WorkflowSettingsTabType) => tabInfo.group === tabsGroup && tabInfo.index === activeTabIndex);
    if (activeTab) {
        return activeTab.draftType;
    }
    return null;
};

export const getEvaluatorIdsAssignedToQuestion = (assessmentRunQuestionDoc: HitData, committeeId?: number): number[] => {
    const assignments: QuestionEvaluatorAssignment[] = assessmentRunQuestionDoc.assignments;
    const evaluatorIds: number[] = [];
    if (Array.isArray(assignments)) {
        assignments.forEach((assignment: QuestionEvaluatorAssignment) => {
            if (!committeeId || committeeId === assignment.committeeId) {
                evaluatorIds.push(assignment.evaluatorId);
            }
        });
    }

    return evaluatorIds;
};

export const getAssignedEvaluatorIdsForQuestion = (arQuestion: HitData, questionUnsavedAssignments: QuestionAssignments, committeeId: number) => {
    const evaluatorIds = new Set<number>();

    if (questionUnsavedAssignments) {
        questionUnsavedAssignments.assignedEvaluatorIds.forEach((evalId: number) => evaluatorIds.add(evalId));
    } else {
        arQuestion.assignments
            .filter((item: QuestionEvaluatorAssignment) => committeeId <= 0 || item.committeeId === committeeId)
            .forEach((assignment: QuestionEvaluatorAssignment) => evaluatorIds.add(assignment.evaluatorId));
    }
    return evaluatorIds;
};

export const getDraftQuestionAssignmentsForQuestion = (unsavedAssignments: QuestionAssignments[], assessmentRunQuestionId: number) => {
    return unsavedAssignments.find((assignment: QuestionAssignments) => assignment.assessmentRunQuestionId === assessmentRunQuestionId);
};

export const areEvaluatorsAssignedForSection = (arQuestions: HitData[], unsavedAssignments: QuestionAssignments[], committeeId: number) => {
    if (!arQuestions || !arQuestions.length) {
        return false;
    }

    const firstQuestionEvaluatorIds = getAssignedEvaluatorIdsForQuestion(
        arQuestions[0],
        getDraftQuestionAssignmentsForQuestion(unsavedAssignments, arQuestions[0].assessmentRunQuestionId),
        committeeId,
    );

    // assignment per section means an evaluator is assigned to all questions and all questions have the same evaluators,
    //  if there are no evaluators assigned to the first question means false
    if (!firstQuestionEvaluatorIds.size) {
        return false;
    }

    const assignmentPerSection = arQuestions.every(
        (arQuestion: HitData) => {
            const questionEvaluatorIds = getAssignedEvaluatorIdsForQuestion(
                arQuestion,
                getDraftQuestionAssignmentsForQuestion(unsavedAssignments, arQuestion.assessmentRunQuestionId),
                committeeId,
            );
            return questionEvaluatorIds.size > 0 && getArraysDifference(firstQuestionEvaluatorIds, questionEvaluatorIds).length === 0;
        }
    );
    return assignmentPerSection;
};

export const isQuestionOverrideWeightActionEnabled = (isDisplayOnlyContext?: boolean) => {
    const { assessment } = store.getState() as RootState;
    const {
        useGradingThresholds,
        isAutomatedTestConstructionEnabled,
        questionWeightOverrideEnabled,
        atLeastOneCandidateConfirmed,
        hasPullingSections,
        hasManualSelectionSections,
    } = assessment;

    if (isApParentTaskWithNotWeightedPoints()) {
        return false;
    }

    return questionWeightOverrideEnabled
        && useGradingThresholds
        && !isAutomatedTestConstructionEnabled
        && (isDisplayOnlyContext
            || (!atLeastOneCandidateConfirmed
                && !isGradingCompleteForCommitteeOrTest()))
        && !assessment.isMultipleAttemptsParent
        && !assessment.isAssessmentPath
        && !hasPullingSections
        && !hasManualSelectionSections;
};

const isQuestionAllocationInCurrentWorkflowPhase = (selectedCommitteeId: number) => {
    const { assessment } = store.getState() as RootState;

    if (!assessment.isGradingWorkflowsEnabledForTest) {
        return false;
    }

    const { workflowPhases, gradingWorkflowDefinition } = assessment;
    if (!workflowPhases || !gradingWorkflowDefinition || selectedCommitteeId <= 0) {
        return false;
    }

    const currentPhase = workflowPhases.find((phase) => phase.committeeId === selectedCommitteeId);
    if (!currentPhase) {
        return false;
    }

    const definitionPhase = gradingWorkflowDefinition.find((phase) => phase.name === currentPhase.name);

    return definitionPhase?.allocationSettings?.allocationUnit === GradingWorkflowAllocationUnit.QUESTION;
};

export const isQuestionAssignmentActionEnabled = () => {
    const { assessment, context } = store.getState() as RootState;
    const { selectedCommitteeId } = context;
    const userHasPlannerRole = hasCurrentUserPlannerPrivilege();
    const userHasEvaluatorRole = hasCurrentUserEvaluatorPrivilege();
    const questionAssignmentEditLevel = getQuestionAssignmentAdminLevelForTestOrCommittee(selectedCommitteeId);

    return assessment.gradingQuestionAssignmentEnabled
        && selectedCommitteeId > -1
        && getMarksModeForTestOrCommittee(selectedCommitteeId) === GRADING_MODE.COLLABORATIVE
        && !assessment.isAutomatedTestConstructionEnabled
        && (userHasPlannerRole || (userHasEvaluatorRole && questionAssignmentEditLevel === QuestionAssignmentEditLevel.PLANNERS_AND_GRADERS))
        && !isGradingCompleteForCommitteeOrTest(selectedCommitteeId)
        && !assessment.isAssessmentPath
        && !assessment.isMultipleAttemptsParent
        && !assessment.isMultipleAttemptsChild
        && !isQuestionAllocationInCurrentWorkflowPhase(selectedCommitteeId);
};

export const isQuestionAnswerKeyEnabled = () => {
    const { assessment } = store.getState() as RootState;
    return assessment.answerKeyCorrectionEnabled
        && !assessment.isAutomatedTestConstructionEnabled
        && !isGradingCompleteForCommitteeOrTest()
        && !assessment.isAssessmentPath
        && !assessment.isMultipleAttemptsParent;
};

export const isApParentTaskWithNotWeightedPoints = () => {
    const { assessment } = store.getState() as RootState;
    const { isAssessmentPath, compositeInfo, compositeGradeCalculationRule } = assessment;

    return isAssessmentPath
        && compositeInfo?.children?.length > 0
        && compositeGradeCalculationRule !== CompositeCalculationRule.ASSESSMENT_PATH_WEIGHTED_POINTS_V1;
};
